import { createAction, createSlice } from "@reduxjs/toolkit";
import { BulkRowError } from "../../types/doctorInvitation.definition";
import {
  BulkStaticDoctorProfileImportRequest,
  StaticDoctorProfileDto,
} from "../../types/staticDoctorProfile.definition";

export type StaticDoctorProfileApiError = {
  code: string;
  message?: string;
  data?: BulkRowError[];
};

// State
interface StaticDoctorProfileState {
  profiles: StaticDoctorProfileDto[];
  loading: boolean;
  error: StaticDoctorProfileApiError | null;
  bulkImportLoading: boolean;
  inviteLoadingId: number | null;
}

const initialState: StaticDoctorProfileState = {
  profiles: [],
  loading: false,
  error: null,
  bulkImportLoading: false,
  inviteLoadingId: null,
};

// Action creators for saga
export const fetchStaticDoctorProfilesRequest = createAction(
  "staticDoctorProfiles/fetchRequest"
);
export const fetchStaticDoctorProfilesSuccess = createAction<
  StaticDoctorProfileDto[]
>("staticDoctorProfiles/fetchSuccess");
export const fetchStaticDoctorProfilesFailure =
  createAction<StaticDoctorProfileApiError>(
    "staticDoctorProfiles/fetchFailure"
  );

export const bulkImportStaticDoctorProfilesRequest =
  createAction<BulkStaticDoctorProfileImportRequest>(
    "staticDoctorProfiles/bulkImportRequest"
  );
export const bulkImportStaticDoctorProfilesSuccess = createAction<
  StaticDoctorProfileDto[]
>("staticDoctorProfiles/bulkImportSuccess");
export const bulkImportStaticDoctorProfilesFailure =
  createAction<StaticDoctorProfileApiError>(
    "staticDoctorProfiles/bulkImportFailure"
  );

export const inviteFromProfileRequest = createAction<number>(
  "staticDoctorProfiles/inviteFromProfileRequest"
);
export const inviteFromProfileSuccess = createAction<StaticDoctorProfileDto>(
  "staticDoctorProfiles/inviteFromProfileSuccess"
);
export const inviteFromProfileFailure =
  createAction<StaticDoctorProfileApiError>(
    "staticDoctorProfiles/inviteFromProfileFailure"
  );

// Slice
const staticDoctorProfileSlice = createSlice({
  name: "staticDoctorProfiles",
  initialState,
  reducers: {
    clearStaticDoctorProfileError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchStaticDoctorProfilesRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaticDoctorProfilesSuccess, (state, action) => {
        state.loading = false;
        state.profiles = action.payload;
        state.error = null;
      })
      .addCase(fetchStaticDoctorProfilesFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(bulkImportStaticDoctorProfilesRequest, state => {
        state.bulkImportLoading = true;
        state.error = null;
      })
      .addCase(bulkImportStaticDoctorProfilesSuccess, (state, action) => {
        state.bulkImportLoading = false;
        // MED-1362: a re-imported profile comes back with its new civility. Replace it in place
        // instead of listing it twice; new profiles go on top, as before.
        const created: StaticDoctorProfileDto[] = [];
        action.payload.forEach(imported => {
          const index = state.profiles.findIndex(p => p.id === imported.id);
          if (index >= 0) {
            state.profiles[index] = imported;
          } else {
            created.push(imported);
          }
        });
        state.profiles.unshift(...created);
        state.error = null;
      })
      .addCase(bulkImportStaticDoctorProfilesFailure, (state, action) => {
        state.bulkImportLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(inviteFromProfileRequest, (state, action) => {
        state.inviteLoadingId = action.payload;
        state.error = null;
      })
      .addCase(inviteFromProfileSuccess, (state, action) => {
        state.inviteLoadingId = null;
        const index = state.profiles.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.profiles[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(inviteFromProfileFailure, (state, action) => {
        state.inviteLoadingId = null;
        state.error = action.payload;
      });
  },
});

// Export slice actions
export const { clearStaticDoctorProfileError } =
  staticDoctorProfileSlice.actions;

// Export selectors
export const selectStaticDoctorProfiles = (state: {
  staticDoctorProfiles: StaticDoctorProfileState;
}) => state.staticDoctorProfiles.profiles;

export const selectStaticDoctorProfilesLoading = (state: {
  staticDoctorProfiles: StaticDoctorProfileState;
}) => state.staticDoctorProfiles.loading;

export const selectStaticDoctorProfilesError = (state: {
  staticDoctorProfiles: StaticDoctorProfileState;
}) => state.staticDoctorProfiles.error;

export const selectBulkImportLoading = (state: {
  staticDoctorProfiles: StaticDoctorProfileState;
}) => state.staticDoctorProfiles.bulkImportLoading;

export const selectInviteLoadingId = (state: {
  staticDoctorProfiles: StaticDoctorProfileState;
}) => state.staticDoctorProfiles.inviteLoadingId;

export default staticDoctorProfileSlice.reducer;
